import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { runWebrtcTeardown } from './webrtc-teardown';

const API_BASE = window.location.origin + '/api';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 35000, // Longer timeout for tunneled requests
  headers: { 'Content-Type': 'application/json' },
});

// ─── Request interceptor: attach JWT token ──────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('cloud_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Response interceptor: standardized error handling ──────────
// All backend responses follow: { success: boolean, message: string, data?: any }
// On error (4xx/5xx), we extract the message and reject with a clear error.
// On 401, we auto-logout and redirect to login.
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Backend returns { success, message, data } — check success flag
    const body = response.data;
    if (body && body.success === false) {
      const err = new Error(body.message || 'Request failed') as any;
      err.statusCode = response.status;
      err.response = response;
      return Promise.reject(err);
    }
    return response;
  },
  async (error: AxiosError) => {
    const status = error.response?.status;
    const body = error.response?.data as any;

    // ── Retry transient tunnel failures for IDEMPOTENT reads only ──────────────
    // The cloud↔local-node tunnel can briefly flap; a single GET (alerts/events/
    // monitoring) then surfaces as a 502/503 ("tunnel not connected") or a network
    // error. Retry those a couple times with backoff so the user doesn't see an
    // intermittent failure. NEVER retry writes (POST/PUT/PATCH/DELETE) — a re-send
    // could double-write — and don't retry timeouts (the node was reachable but slow;
    // a retry just stacks another 35s wait).
    const config = error.config as (typeof error.config & { __retryCount?: number }) | undefined;
    const method = (config?.method || 'get').toLowerCase();
    const isIdempotent = method === 'get' || method === 'head';
    const isTimeout =
      error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || /timeout/i.test(error.message || '');
    const isTransient = status === 502 || status === 503 || (!error.response && !isTimeout);
    const MAX_RETRIES = 2;
    if (config && isIdempotent && isTransient && status !== 401) {
      config.__retryCount = (config.__retryCount || 0) + 1;
      if (config.__retryCount <= MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, 400 * (config.__retryCount as number))); // 400ms, 800ms
        return api.request(config);
      }
    }

    // Auto-logout on 401 (token expired/invalid)
    if (status === 401) {
      // Gracefully close SFU sessions before the token is gone — same path as
      // manual logout, so a forced/expired session doesn't leak a live PC.
      runWebrtcTeardown();
      localStorage.removeItem('cloud_token');
      localStorage.removeItem('cloud_user');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(new Error(body?.message || 'Session expired. Please login again.'));
    }

    // Extract standardized error message from backend
    const message = body?.message || error.message || 'Network error';
    const enhancedError = new Error(message) as any;
    enhancedError.statusCode = status;
    enhancedError.response = error.response;
    return Promise.reject(enhancedError);
  }
);

/**
 * Helper: unwrap double-wrapped tunnel proxy responses.
 * Tunnel proxy returns { success, data: { success, message, data: actualData } }
 * After axios resp.data.data we get { success, message, data: actualData } OR actualData directly.
 * This normalizes it to always return the innermost data.
 */
function unwrap(outer: any): any {
  if (outer && typeof outer === 'object' && 'success' in outer && 'data' in outer) {
    return outer.data;
  }
  return outer;
}

// ─── Auth (Cloud FE → Cloud BE direct) ────────────────────────

/** POST /auth/login — Cloud FE → Cloud BE (direct) */
// export async function login(email: string, password: string) {
//   const resp = await api.post('/auth/login', { email, password });
//   localStorage.setItem('cloud_token', resp.data.data.token);
//   localStorage.setItem('cloud_user', JSON.stringify(resp.data.data.user));
//   return resp.data.data;
// }

export async function login(email: string, password: string) {
  const resp = await api.post('/auth/login', { email, password });
  const data = resp.data.data;
  localStorage.setItem('cloud_token', data.token);
  localStorage.setItem('cloud_user', JSON.stringify({ ...data.user, permissions: data.permissions }));
  return data;
}


/** POST /auth/register — Cloud FE → Cloud BE (direct) */
// export async function register(email: string, password: string, name: string) {
//   const resp = await api.post('/auth/register', { email, password, name });
//   localStorage.setItem('cloud_token', resp.data.data.token);
//   localStorage.setItem('cloud_user', JSON.stringify(resp.data.data.user));
//   return resp.data.data;
// }

export async function register(email: string, password: string, name: string) {
  const resp = await api.post('/auth/register', { email, password, name });
  const data = resp.data.data;
  localStorage.setItem('cloud_token', data.token);
  localStorage.setItem('cloud_user', JSON.stringify({ ...data.user, permissions: data.permissions }));
  return data;
}

/** GET /auth/me — Cloud FE → Cloud BE (direct) */
export async function getMe() {
  const resp = await api.get('/auth/me');
  return resp.data.data;
}

/**
 * Set (or clear, with null) MY favourite site — the scope a fresh session opens on.
 * Self-service, so it needs no user-management permission.
 */
export async function setDefaultSite(nodeId: string | null) {
  const resp = await api.put('/auth/me/default-site', { node_id: nodeId });
  return resp.data.data;
}

export async function logout() {
  // Close SFU sessions gracefully (bypasses the resume grace) BEFORE clearing
  // the token and reloading — otherwise the persistent warm connection would
  // outlive the session and linger for the full grace window.
  runWebrtcTeardown();
  // Revoke the token server-side so it can't be reused before it expires.
  // Best-effort: the token is still in localStorage here (removed below), so the
  // request interceptor attaches it; proceed with local logout regardless.
  try {
    await api.post('/auth/logout', {}, { timeout: 3000 });
  } catch { /* ignore — always complete the local logout */ }
  localStorage.removeItem('cloud_token');
  localStorage.removeItem('cloud_user');
  window.location.href = '/login';
}

export function getStoredUser() {
  const user = localStorage.getItem('cloud_user');
  return user ? JSON.parse(user) : null;
}

export function getStoredToken() {
  return localStorage.getItem('cloud_token');
}

// ─── Nodes (Cloud FE → Cloud BE direct) ───────────────────────

/** GET /nodes — Cloud FE → Cloud BE (direct) */
export async function getNodes() {
  const resp = await api.get('/nodes');
  return resp.data.data;
}

/** GET /nodes/:nodeId — Cloud FE → Cloud BE (direct) */
export async function getNode(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}`);
  return resp.data.data;
}

/** POST /nodes/register — Cloud FE → Cloud BE (direct) */
export async function registerNode(nodeId: string, name: string, authToken: string) {
  const resp = await api.post('/nodes/register', { nodeId, name, authToken });
  return resp.data.data;
}

/** PUT /nodes/:nodeId — Cloud FE → Cloud BE (direct) */
export async function updateNode(nodeId: string, data: { name: string; authToken?: string }) {
  const resp = await api.put(`/nodes/${nodeId}`, data);
  return resp.data.data;
}

/** DELETE /nodes/:nodeId — Cloud FE → Cloud BE (direct) */
export async function deleteNode(nodeId: string) {
  const resp = await api.delete(`/nodes/${nodeId}`);
  return resp.data.data;
}

/** POST /nodes/:nodeId/access — Cloud FE → Cloud BE (direct) */
export async function grantNodeAccess(nodeId: string, userId: string, permissions: string) {
  const resp = await api.post(`/nodes/${nodeId}/access`, { userId, permissions });
  return resp.data.data;
}

// ─── Node Proxy (Cloud FE → Cloud BE → Tunnel → Local BE) ────

/**
 * GET /nodes/:nodeId/cameras — Cloud FE → Cloud BE → Tunnel → Local BE
 *
 * `groupIds` filters on the NODE, recursively (a group's sub-groups count), which is what the
 * local dashboard's filter does. Omit it for the whole list — every existing caller does.
 */
export async function getNodeCameras(nodeId: string, opts?: { groupIds?: string[]; search?: string }) {
  const groupIds = (opts?.groupIds || []).filter(Boolean);
  const resp = await api.get(`/nodes/${nodeId}/cameras`, {
    params: {
      limit: 1000,
      ...(groupIds.length ? { group_id_recursive: groupIds.join(',') } : {}),
      ...(opts?.search ? { search: opts.search } : {}),
    },
  });
  return unwrap(resp.data.data);
}

/**
 * GET /nodes/:nodeId/cameras/names — id + name only, for filter dropdowns and
 * id→name labelling. Needs only `cameras.list`, so modules like attendance can
 * use it without being granted cameras.view (camera details + live video).
 */
export async function getNodeCameraNames(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/cameras/names`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/cameras/:cameraId/snapshot — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getCameraSnapshot(nodeId: string, cameraId: string) {
  const resp = await api.get(`/nodes/${nodeId}/cameras/${cameraId}/snapshot`, {
    timeout: 20000,
  });
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/cameras/discover — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function discoverCameras(nodeId: string) {
  const resp = await api.post(`/nodes/${nodeId}/cameras/discover`);
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/cameras — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function addCamera(nodeId: string, data: any) {
  const resp = await api.post(`/nodes/${nodeId}/cameras`, data);
  return unwrap(resp.data.data);
}

/** PUT /nodes/:nodeId/cameras/:cameraId — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function editCamera(nodeId: string, cameraId: string, data: any) {
  const resp = await api.put(`/nodes/${nodeId}/cameras/${cameraId}`, data);
  return unwrap(resp.data.data);
}

/** DELETE /nodes/:nodeId/cameras/:cameraId — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function deleteCamera(nodeId: string, cameraId: string) {
  const resp = await api.delete(`/nodes/${nodeId}/cameras/${cameraId}`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/cameras/:cameraId/status — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getCameraStatus(nodeId: string, cameraId: string) {
  const resp = await api.get(`/nodes/${nodeId}/cameras/${cameraId}/status`);
  return unwrap(resp.data.data);
}

// ─── Groups (Cloud FE → Cloud BE → Tunnel → Local BE) ────────

/** GET /nodes/:nodeId/groups — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodeGroups(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/groups`);
  return unwrap(resp.data.data);
}

/**
 * GET /nodes/:nodeId/groups/for-filter — flat group list scoped to the user's
 * accessible cameras on the node. No groups.view required; use for the camera
 * filter when the user can see cameras but not manage groups.
 */
export async function getNodeGroupsForFilter(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/groups/for-filter`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/groups/:groupId/cameras — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getGroupCameras(nodeId: string, groupId: string, recursive = true) {
  const resp = await api.get(`/nodes/${nodeId}/groups/${groupId}/cameras`, { params: { recursive } });
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/groups — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function createGroup(nodeId: string, data: any) {
  const resp = await api.post(`/nodes/${nodeId}/groups`, data);
  return unwrap(resp.data.data);
}

/** PUT /nodes/:nodeId/groups/:groupId — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function updateGroup(nodeId: string, groupId: string, data: any) {
  const resp = await api.put(`/nodes/${nodeId}/groups/${groupId}`, data);
  return unwrap(resp.data.data);
}

/** DELETE /nodes/:nodeId/groups/:groupId — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function deleteGroup(nodeId: string, groupId: string) {
  const resp = await api.delete(`/nodes/${nodeId}/groups/${groupId}`);
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/groups/:groupId/cameras — Add cameras to group */
export async function addCamerasToGroup(nodeId: string, groupId: string, cameraIds: string[]) {
  const resp = await api.post(`/nodes/${nodeId}/groups/${groupId}/cameras`, { camera_ids: cameraIds });
  return unwrap(resp.data.data);
}

/** DELETE /nodes/:nodeId/groups/:groupId/cameras/:cameraId — Remove camera from group */
export async function removeCameraFromGroup(nodeId: string, groupId: string, cameraId: string) {
  const resp = await api.delete(`/nodes/${nodeId}/groups/${groupId}/cameras/${cameraId}`);
  return unwrap(resp.data.data);
}

// ─── Map View layouts (Cloud FE → Cloud BE → Tunnel → Local BE) ─
//
// Layouts live on the NODE beside the cameras they describe, so both dashboards read the
// same floor plans. `getNodeMapLayout` carries the plan image inline as base64 — the tunnel
// is a JSON channel with no binary route to proxy.

/** GET /nodes/:nodeId/map-layouts — layout metadata (no image bytes). */
export async function getNodeMapLayouts(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/map-layouts`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/map-layouts/:layoutId — layout + placements + inline image. */
export async function getNodeMapLayout(nodeId: string, layoutId: string) {
  const resp = await api.get(`/nodes/${nodeId}/map-layouts/${layoutId}`);
  return unwrap(resp.data.data);
}

/**
 * GET /nodes/:nodeId/analytics/summary — the site dashboard's AI analytics row.
 *
 * One call for every figure: per-active-model counts, alert severities and presence. The window
 * ("today since local midnight") is decided on the NODE, so the site's own day is what the cards
 * report rather than the browser's or the cloud's.
 *
 * `surfaces` is the site's active model surfaces (shared useActiveModelSurfaces) — the node
 * queries only those, so a site running one detector costs one query set.
 */
export async function getNodeAiSummary(
  nodeId: string,
  surfaces: string[],
  opts: { alerts?: boolean; attendance?: boolean; fresh?: boolean } = {},
) {
  const resp = await api.get(`/nodes/${nodeId}/analytics/summary`, {
    params: {
      surfaces: surfaces.join(','),
      ...(opts.alerts === false ? { alerts: 0 } : {}),
      ...(opts.attendance === false ? { attendance: 0 } : {}),
      // Only the Refresh button sends this — the poll rides the node's cache.
      ...(opts.fresh ? { fresh: 1 } : {}),
    },
  });
  return unwrap(resp.data.data);
}

/**
 * GET /nodes/:nodeId/analytics/occupancy-series — the Person Count report's chart.
 *
 * Per-camera occupancy over the window, bucketed on the NODE. `from`/`to` are epoch SECONDS;
 * omit them and the node answers for its OWN day, which is the right default when the operator
 * reading this may be in a different timezone from the site.
 */
export async function getNodeOccupancySeries(
  nodeId: string,
  params: { from?: number; to?: number; camera_ids?: string; bucket?: number } = {},
) {
  const resp = await api.get(`/nodes/${nodeId}/analytics/occupancy-series`, {
    params: {
      ...(params.from != null ? { from: params.from } : {}),
      ...(params.to != null ? { to: params.to } : {}),
      ...(params.camera_ids ? { camera_ids: params.camera_ids } : {}),
      ...(params.bucket ? { bucket: params.bucket } : {}),
    },
  });
  return unwrap(resp.data.data);
}

// ─── Live Wall (per site) — Cloud FE → Cloud BE → Tunnel → Local BE ──────────
//
// The layouts live on the NODE, so this app's per-site Live Wall and that node's own local
// dashboard show one set of grids. Reads arrive with each slot's camera name already resolved
// from the node's cameras table and slots outside the caller's camera access already dropped.

export interface SiteLiveWallSlot {
  cameraId: string;
  cameraName: string;
  gridPosition: number;
}

export interface SiteLiveWallView {
  id: string;
  name: string;
  cameras: SiteLiveWallSlot[];
  grid_size: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export async function getNodeLiveWallViews(nodeId: string): Promise<SiteLiveWallView[]> {
  const resp = await api.get(`/nodes/${nodeId}/live-wall/views`);
  return unwrap(resp.data.data) || [];
}

/** `null` rather than a 404 when nobody has starred a layout on this node yet. */
export async function getNodeLiveWallDefault(nodeId: string): Promise<SiteLiveWallView | null> {
  const resp = await api.get(`/nodes/${nodeId}/live-wall/views/default`);
  return unwrap(resp.data.data) || null;
}

export async function createNodeLiveWallView(
  nodeId: string,
  body: { name: string; cameras: SiteLiveWallSlot[]; gridSize: string },
): Promise<SiteLiveWallView> {
  const resp = await api.post(`/nodes/${nodeId}/live-wall/views`, body);
  return unwrap(resp.data.data);
}

export async function updateNodeLiveWallView(
  nodeId: string,
  viewId: string,
  body: { name?: string; cameras?: SiteLiveWallSlot[]; gridSize?: string },
): Promise<SiteLiveWallView> {
  const resp = await api.put(`/nodes/${nodeId}/live-wall/views/${viewId}`, body);
  return unwrap(resp.data.data);
}

export async function deleteNodeLiveWallView(nodeId: string, viewId: string) {
  await api.delete(`/nodes/${nodeId}/live-wall/views/${viewId}`);
}

export async function setNodeLiveWallDefault(nodeId: string, viewId: string): Promise<SiteLiveWallView> {
  const resp = await api.put(`/nodes/${nodeId}/live-wall/views/${viewId}/set-default`);
  return unwrap(resp.data.data);
}

export async function clearNodeLiveWallDefault(nodeId: string, viewId: string): Promise<SiteLiveWallView> {
  const resp = await api.delete(`/nodes/${nodeId}/live-wall/views/${viewId}/set-default`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/map-layouts/placed-cameras — cameraId → the layout holding it. */
export async function getNodeMapPlacedCameras(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/map-layouts/placed-cameras`);
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/map-layouts */
export async function createNodeMapLayout(nodeId: string, data: any) {
  const resp = await api.post(`/nodes/${nodeId}/map-layouts`, data);
  return unwrap(resp.data.data);
}

/** PATCH /nodes/:nodeId/map-layouts/:layoutId */
export async function updateNodeMapLayout(nodeId: string, layoutId: string, data: any) {
  const resp = await api.patch(`/nodes/${nodeId}/map-layouts/${layoutId}`, data);
  return unwrap(resp.data.data);
}

/** DELETE /nodes/:nodeId/map-layouts/:layoutId */
export async function deleteNodeMapLayout(nodeId: string, layoutId: string) {
  const resp = await api.delete(`/nodes/${nodeId}/map-layouts/${layoutId}`);
  return unwrap(resp.data.data);
}

/** PUT /nodes/:nodeId/map-layouts/:layoutId/placements — replaces the whole pin set. */
export async function setNodeMapPlacements(nodeId: string, layoutId: string, placements: any[]) {
  const resp = await api.put(`/nodes/${nodeId}/map-layouts/${layoutId}/placements`, { placements });
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/cameras (paginated) — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodeCamerasPaginated(nodeId: string, params: { page?: number; limit?: number; search?: string }) {
  const resp = await api.get(`/nodes/${nodeId}/cameras`, { params: { limit: params.limit || 20, page: params.page || 1 } });
  const outer = resp.data.data;
  // Need to preserve pagination info alongside data array
  if (outer && typeof outer === 'object' && 'success' in outer && 'data' in outer) {
    // Double-wrapped: outer = { success, message, data: [...], pagination: {...} }
    return { data: outer.data, pagination: outer.pagination, total: outer.pagination?.total };
  }
  return outer;
}

// ─── Discovery (Cloud FE → Cloud BE → Tunnel → Local BE) ─────

/** POST /nodes/:nodeId/discovery/scan — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function startDiscovery(nodeId: string, subnet?: string) {
  const resp = await api.post(`/nodes/${nodeId}/discovery/scan`, { subnet });
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/discovery/results — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getDiscoveryResults(nodeId: string, params?: { page?: number; limit?: number; search?: string }) {
  const resp = await api.get(`/nodes/${nodeId}/discovery/results`, { params });
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/discovery/bulk-approve — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function bulkApproveDiscovery(nodeId: string, cameras: any[]) {
  const resp = await api.post(`/nodes/${nodeId}/discovery/bulk-approve`, { cameras });
  return unwrap(resp.data.data);
}

// ─── Recordings (Cloud FE → Cloud BE → Tunnel → Local BE) ────

/** GET /nodes/:nodeId/recordings/dates — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getRecordingDates(nodeId: string, cameraId: string) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/dates`, { params: { cameraId, tz_offset: new Date().getTimezoneOffset() } });
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/recordings/timeline — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getRecordingTimeline(nodeId: string, cameraId: string, date: string) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/timeline`, { params: { cameraId, date, tz_offset: new Date().getTimezoneOffset() } });
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/recordings/segments — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getRecordingSegments(nodeId: string, cameraId: string, startTime: string, endTime: string) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/segments`, { params: { cameraId, start_time: startTime, end_time: endTime } });
  return unwrap(resp.data.data);
}

/**
 * GET /nodes/:nodeId/recordings/storage-by-camera — per-camera archive summary (size, segment
 * count, span) for the recordings picker. One tunnel round trip for every camera.
 */
export async function getRecordingStorageByCamera(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/storage-by-camera`);
  return unwrap(resp.data.data);
}

// ─── Recording mode configuration (continuous / event / motion) ───────────────
// Every call is Cloud FE → Cloud BE → Tunnel → Local BE /api/internal/recordings/config*,
// where the acting-user middleware re-applies the same capability checks as the local
// dashboard. A cloud operator can never exceed what that user could do locally.

/** GET /nodes/:nodeId/recordings/config — all configs + system defaults + form options */
export async function getRecordingConfigs(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/config`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/recordings/config/:cameraId */
export async function getRecordingConfig(nodeId: string, cameraId: string) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/config/${cameraId}`);
  return unwrap(resp.data.data);
}

/** PUT /nodes/:nodeId/recordings/config/:cameraId */
export async function updateRecordingConfig(nodeId: string, cameraId: string, config: any) {
  const resp = await api.put(`/nodes/${nodeId}/recordings/config/${cameraId}`, config);
  return unwrap(resp.data.data);
}

/** PUT /nodes/:nodeId/recordings/config/bulk */
export async function bulkUpdateRecordingConfig(nodeId: string, cameraIds: string[], config: any) {
  const resp = await api.put(`/nodes/${nodeId}/recordings/config/bulk`, {
    camera_ids: cameraIds,
    config,
  });
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/recordings/trigger/:cameraId — manual "record now" */
export async function triggerRecording(nodeId: string, cameraId: string, durationSeconds = 30) {
  const resp = await api.post(`/nodes/${nodeId}/recordings/trigger/${cameraId}`, {
    duration_seconds: durationSeconds,
  });
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/recordings/motion-score/:cameraId — live meter (stream + ONVIF state) */
export async function getMotionScore(nodeId: string, cameraId: string) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/motion-score/${cameraId}`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/recordings/motion-capability/:cameraId */
export async function getMotionCapability(nodeId: string, cameraId: string) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/motion-capability/${cameraId}`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/recordings/triggers/:cameraId — retention-window audit log */
export async function getRecordingTriggers(
  nodeId: string,
  cameraId: string,
  from?: string,
  to?: string
) {
  const resp = await api.get(`/nodes/${nodeId}/recordings/triggers/${cameraId}`, {
    params: { from, to },
  });
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/live/start — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function startLive(nodeId: string, cameraId: string) {
  const resp = await api.post(`/nodes/${nodeId}/live/start`, { cameraId });
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/live/stop — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function stopLive(nodeId: string, cameraId: string) {
  const resp = await api.post(`/nodes/${nodeId}/live/stop`, { cameraId });
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/recordings — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getRecordings(nodeId: string, params?: any) {
  const resp = await api.get(`/nodes/${nodeId}/recordings`, { params });
  return unwrap(resp.data.data);
}

// ─── Models (Cloud FE → Cloud BE → Tunnel → Local BE) ────────

/** GET /nodes/:nodeId/models — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodeModels(nodeId: string, params?: any) {
  const resp = await api.get(`/nodes/${nodeId}/models`, { params });
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/models — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function createNodeModel(nodeId: string, data: any) {
  const resp = await api.post(`/nodes/${nodeId}/models`, data);
  return unwrap(resp.data.data);
}

/** PATCH /nodes/:nodeId/models/:modelId — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function updateNodeModel(nodeId: string, modelId: string, data: any) {
  const resp = await api.patch(`/nodes/${nodeId}/models/${modelId}`, data);
  return unwrap(resp.data.data);
}

/** DELETE /nodes/:nodeId/models/:modelId — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function deleteNodeModel(nodeId: string, modelId: string) {
  const resp = await api.delete(`/nodes/${nodeId}/models/${modelId}`);
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/models/:modelId/cameras/:cameraId — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function attachModelToCamera(nodeId: string, modelId: string, cameraId: string, config?: any) {
  const resp = await api.post(`/nodes/${nodeId}/models/${modelId}/cameras/${cameraId}`, config || {});
  return unwrap(resp.data.data);
}

/** DELETE /nodes/:nodeId/models/:modelId/cameras/:cameraId — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function detachModelFromCamera(nodeId: string, modelId: string, cameraId: string) {
  const resp = await api.delete(`/nodes/${nodeId}/models/${modelId}/cameras/${cameraId}`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/cameras/:cameraId/models — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getCameraModels(nodeId: string, cameraId: string) {
  const resp = await api.get(`/nodes/${nodeId}/cameras/${cameraId}/models`);
  return unwrap(resp.data.data);
}

/** PUT /nodes/:nodeId/cameras/:cameraId/live-mode — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function setCameraLiveMode(nodeId: string, cameraId: string, mode: string) {
  const resp = await api.put(`/nodes/${nodeId}/cameras/${cameraId}/live-mode`, { mode });
  return unwrap(resp.data.data);
}

// ─── TURN (Cloud FE → Cloud BE direct) ───────────────────────

/** GET /turn/status — Cloud FE → Cloud BE (direct) */
export async function getTurnStatus() {
  const resp = await api.get('/turn/status');
  return unwrap(resp.data.data);
}

/** GET /turn/sessions — Cloud FE → Cloud BE (direct) */
export async function getTurnSessions() {
  const resp = await api.get('/turn/sessions');
  return unwrap(resp.data.data);
}

/** GET /turn/metrics — Cloud FE → Cloud BE (direct) */
export async function getTurnMetrics(minutes: number = 60) {
  const resp = await api.get('/turn/metrics', { params: { minutes } });
  return unwrap(resp.data.data);
}

/** GET /turn/connections — Cloud FE → Cloud BE (direct) */
export async function getTurnConnections() {
  const resp = await api.get('/turn/connections');
  return unwrap(resp.data.data);
}

// ─── Health (Cloud FE → Cloud BE direct) ──────────────────────

/** GET /health — Cloud FE → Cloud BE (direct) */
export async function getHealth() {
  const resp = await api.get('/health');
  return unwrap(resp.data.data);
}

// ─── Monitoring (Cloud FE → Cloud BE direct) ─────────────────

/** GET /monitoring/system — Cloud FE → Cloud BE (direct) */
export async function getMonitoringSystem() {
  const resp = await api.get('/monitoring/system');
  return unwrap(resp.data.data);
}

/** GET /monitoring/services — Cloud FE → Cloud BE (direct) */
export async function getMonitoringServices() {
  const resp = await api.get('/monitoring/services');
  return unwrap(resp.data.data);
}

/** GET /monitoring/sockets — Cloud FE → Cloud BE (direct) */
export async function getMonitoringSockets() {
  const resp = await api.get('/monitoring/sockets');
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes — Cloud FE → Cloud BE (direct) */
export async function getMonitoringLocalNodes() {
  const resp = await api.get('/monitoring/local-nodes');
  return unwrap(resp.data.data);
}

/** GET /monitoring/system/history — Cloud FE → Cloud BE (direct) */
export async function getMonitoringHistory(range: string = '1h') {
  const resp = await api.get(`/monitoring/system/history?range=${range}`);
  return unwrap(resp.data.data);
}

/** POST /monitoring/local-nodes/:nodeId/toggle-alerts — Cloud FE → Cloud BE (direct) */
export async function toggleNodeAlerts(nodeId: string) {
  const resp = await api.post(`/monitoring/local-nodes/${nodeId}/toggle-alerts`);
  return unwrap(resp.data.data);
}

// ─── Node Monitoring Proxy (Cloud FE → Cloud BE → Tunnel → Local BE) ─

/** GET /monitoring/local-nodes/:nodeId/proxy/monitoring/system — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodeMonitoringSystem(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/proxy/monitoring/system`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/proxy/monitoring/system/history — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodeMonitoringHistory(nodeId: string, range: string = '1h', machineName?: string) {
  let url = `/monitoring/local-nodes/${nodeId}/proxy/monitoring/system/history?range=${range}`;
  if (machineName && machineName !== 'all') url += `&machine_name=${machineName}`;
  const resp = await api.get(url);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/proxy/monitoring/cloud-tunnel — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodeMonitoringCloudTunnel(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/proxy/monitoring/cloud-tunnel`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/proxy/monitoring/recording-playback — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodeMonitoringRecPlayback(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/proxy/monitoring/recording-playback`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/proxy/system/pipelines — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodePipelines(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/proxy/system/pipelines`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/proxy/monitoring/sfu — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodeSfuSessions(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/proxy/monitoring/sfu`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/uptime — Cloud FE → Cloud BE (direct, from DB) */
export async function getNodeUptimeLog(nodeId: string, limit: number = 100) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/uptime?limit=${limit}`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/proxy/monitoring/machines — Cloud FE → Cloud BE → Tunnel → Local BE */
export async function getNodeMonitoringMachines(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/proxy/monitoring/machines`);
  return unwrap(resp.data.data);
}

// ─── Analytics (Cloud FE → Cloud BE → Tunnel → Local Analytics Service) ─

/** GET /monitoring/local-nodes/:nodeId/analytics/events — Cloud FE → Cloud BE → Tunnel → Local Analytics */
export async function getNodeAnalyticsEvents(nodeId: string, params?: Record<string, any>) {
  const query = params ? '?' + new URLSearchParams(params as any).toString() : '';
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/events${query}`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/analytics/events/stats — Cloud FE → Cloud BE → Tunnel → Local Analytics */
export async function getNodeAnalyticsStats(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/events/stats`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/analytics/events/:eventId/images/:imageType — Cloud FE → Cloud BE → Tunnel → Local Analytics (binary) */
export async function getNodeAnalyticsEventImage(nodeId: string, eventId: string, imageType: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/events/${eventId}/images/${imageType}`, {
    responseType: 'blob',
  });
  return resp.data;
}

// ─── Alert Investigation (Cloud FE → Cloud BE → Tunnel → Local Analytics Service) ───

/** GET /monitoring/local-nodes/:nodeId/analytics/events/:model — model-specific events */
export async function getNodeModelEvents(nodeId: string, model: string, params?: Record<string, any>) {
  const query = params ? '?' + new URLSearchParams(params as any).toString() : '';
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/events/${model}${query}`);
  // Return full response (with pagination) — don't unwrap to array
  const inner = resp.data.data;
  return inner && typeof inner === 'object' && 'data' in inner ? inner : { data: inner, pagination: null };
}

/**
 * GET /monitoring/local-nodes/:nodeId/analytics/events/:model/:eventId — one event.
 *
 * `:eventId` resolves against either the AI `event_id` or the DB id (findEventRow in the
 * command-center), so a caller holding only the AI id — the attendance timeline's
 * `?event_id=` deep link — reaches the same row.
 */
export async function getNodeModelEvent(nodeId: string, model: string, eventId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/events/${model}/${encodeURIComponent(eventId)}`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/analytics/events/:model/:eventId/images/:imageType — model event image */
export async function getNodeModelEventImage(nodeId: string, model: string, eventId: string, imageType: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/events/${model}/${eventId}/images/${imageType}`, {
    responseType: 'blob',
  });
  return resp.data;
}

// ─── Alert config rules (Cloud FE → Cloud BE → Tunnel → Local command-center /api/config) ───

/**
 * The /nodes proxy, NOT the monitoring catch-all — same reason as `alertsWriteBase` below: the
 * generic monitoring write-proxies were removed in the RBAC pass, so every save, toggle,
 * duplicate and delete of a rule 404'd while the page still listed rules fine (the monitoring
 * GET proxy survived). These routes carry `alerts.config` and run the node-access check.
 *
 * Both the proxy and the tunnel pass the command-center's own `{ success, data }` body through
 * untouched, so responses come back double-wrapped — hence `unwrap` on each, since the page puts
 * the returned rule straight into its list.
 */
const cfgBase = (nodeId: string) => `/nodes/${nodeId}/analytics/config`;

/** List a node's alert-config rules. */
export async function getNodeAlertConfigs(nodeId: string) {
  const resp = await api.get(cfgBase(nodeId));
  const inner = unwrap(resp.data.data);
  return Array.isArray(inner) ? inner : [];
}

/** Create an alert-config rule on a node. */
export async function createNodeAlertConfig(nodeId: string, data: any) {
  const resp = await api.post(cfgBase(nodeId), data);
  return unwrap(resp.data.data);
}

/** Replace an alert-config rule. */
export async function updateNodeAlertConfig(nodeId: string, id: string, data: any) {
  const resp = await api.put(`${cfgBase(nodeId)}/${id}`, data);
  return unwrap(resp.data.data);
}

/** Toggle a rule active/inactive. */
export async function toggleNodeAlertConfig(nodeId: string, id: string) {
  const resp = await api.patch(`${cfgBase(nodeId)}/${id}/toggle`);
  return unwrap(resp.data.data);
}

/** Duplicate a rule. */
export async function duplicateNodeAlertConfig(nodeId: string, id: string) {
  const resp = await api.post(`${cfgBase(nodeId)}/${id}/duplicate`);
  return unwrap(resp.data.data);
}

/** Delete a rule. */
export async function deleteNodeAlertConfig(nodeId: string, id: string) {
  await api.delete(`${cfgBase(nodeId)}/${id}`);
}

// ─── Realtime Monitoring (Cloud FE → Cloud BE → Tunnel → Local BE /api/monitoring) ───

/** GET /monitoring/local-nodes/:nodeId/realtime-stats — hierarchy + alert counts */
export async function getNodeRealtimeStats(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/realtime-stats`);
  return unwrap(resp.data.data);
}

/**
 * GET /monitoring/local-nodes/:nodeId/demo-reset — does this site wipe itself on a timer?
 *
 * Drives the title-bar demo banner on pages that hold no alert-board socket. Shape:
 * { enabled, interval_minutes, next_reset_at, last_reset_at }.
 */
export async function getNodeDemoReset(nodeId: string) {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/demo-reset`);
  return unwrap(resp.data.data);
}

/** GET /monitoring/local-nodes/:nodeId/realtime-alerts — latest alerts */
export async function getNodeRealtimeAlerts(nodeId: string, params?: Record<string, any>) {
  const query = params ? '?' + new URLSearchParams(params as any).toString() : '';
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/realtime-alerts${query}`);
  return unwrap(resp.data.data);
}

// ─── Alert lifecycle (Cloud FE → Cloud BE → Tunnel → Local command-center /api/alerts) ───

const alertsBase = (nodeId: string) => `/monitoring/local-nodes/${nodeId}/analytics/alerts`;

/**
 * Writes go through the /nodes proxy, NOT the monitoring catch-all: the generic
 * POST/PATCH monitoring write-proxies were removed in the RBAC pass, so every
 * lifecycle call and comment on that path 404'd. These routes are gated on
 * `alerts.edit` and return the local body under `data`.
 */
const alertsWriteBase = (nodeId: string) => `/nodes/${nodeId}/analytics/alerts`;

/** List a node's alerts (full pagination object: { data, pagination }). */
export async function getNodeAlerts(nodeId: string, params?: Record<string, any>) {
  const query = params ? '?' + new URLSearchParams(params as any).toString() : '';
  const resp = await api.get(`${alertsBase(nodeId)}${query}`);
  // Cloud wraps the local payload under data; the local list returns { data, pagination }.
  return resp.data.data;
}

/**
 * Aggregate alert counts for one node (`{ total, unacknowledged, by_severity, by_status, … }`).
 * Goes through the /nodes proxy — which has an explicit `/analytics/alerts/stats`
 * route ahead of `/analytics/alerts/:id` — rather than the monitoring catch-all,
 * so it stays gated on `alerts.view` instead of `monitoring.view`.
 */
export async function getNodeAlertStats(nodeId: string, params?: Record<string, any>) {
  const resp = await api.get(`/nodes/${nodeId}/analytics/alerts/stats`, { params });
  const payload = resp.data?.data;
  return payload?.data ?? payload;
}

/** A single node alert by id (for the alert-detail page). */
export async function getNodeAlert(nodeId: string, id: string) {
  const resp = await api.get(`${alertsBase(nodeId)}/${id}`);
  // CC get-by-id returns { success, data: <alert row> }; the cloud proxy re-wraps under data.
  const payload = resp.data?.data;
  return payload?.data ?? payload;
}

/** The node's users (local + provisioned cloud users with access), for the assign dropdown. */
export async function getNodeUsers(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/users`);
  const payload = resp.data?.data;
  return (Array.isArray(payload) ? payload : payload?.data) ?? [];
}

export async function acknowledgeNodeAlert(nodeId: string, id: string, userId: string | null, userName?: string | null) {
  const resp = await api.patch(`${alertsWriteBase(nodeId)}/${id}/acknowledge`, { user_id: userId, user_name: userName });
  return unwrap(resp.data.data);
}
export async function unacknowledgeNodeAlert(nodeId: string, id: string, userId?: string | null, userName?: string | null) {
  const resp = await api.patch(`${alertsWriteBase(nodeId)}/${id}/unacknowledge`, { user_id: userId, user_name: userName });
  return unwrap(resp.data.data);
}
export async function assignNodeAlert(nodeId: string, id: string, assigneeId: string, userId: string | null, assigneeName?: string | null, userName?: string | null) {
  const resp = await api.patch(`${alertsWriteBase(nodeId)}/${id}/assign`, {
    assignee_id: assigneeId,
    user_id: userId,
    assignee_name: assigneeName,
    user_name: userName,
  });
  return unwrap(resp.data.data);
}
export async function resolveNodeAlert(nodeId: string, id: string, userId: string | null, note: string, userName?: string | null) {
  const resp = await api.patch(`${alertsWriteBase(nodeId)}/${id}/resolve`, { user_id: userId, note, user_name: userName });
  return unwrap(resp.data.data);
}
export async function cancelNodeAlert(nodeId: string, id: string, userId: string | null, reason: string, userName?: string | null) {
  const resp = await api.patch(`${alertsWriteBase(nodeId)}/${id}/cancel`, { user_id: userId, reason, user_name: userName });
  return unwrap(resp.data.data);
}
/**
 * Permanently delete a node alert. Gated on `alerts.delete` at the cloud proxy, then relayed
 * to the node's command-center as `DELETE /api/alerts/:id`. There is no undo — callers confirm.
 */
export async function deleteNodeAlert(nodeId: string, id: string) {
  const resp = await api.delete(`${alertsWriteBase(nodeId)}/${id}`);
  return unwrap(resp.data?.data);
}
/** Per-alert action history: who assigned/acked/resolved/reopened/commented. */
export async function getNodeAlertActions(nodeId: string, id: string) {
  const resp = await api.get(`${alertsWriteBase(nodeId)}/${id}/actions`);
  return unwrap(resp.data.data);
}
/** Add a comment to a node alert. */
export async function addNodeAlertComment(nodeId: string, id: string, userId: string | null, userName: string | null, comment: string) {
  const resp = await api.post(`${alertsWriteBase(nodeId)}/${id}/comments`, { user_id: userId, user_name: userName, comment });
  return unwrap(resp.data.data);
}

// ─── Heatmap (Cloud FE → Cloud BE → Tunnel → Local Analytics Service) ───

/** GET /monitoring/local-nodes/:nodeId/analytics/heatmap/aggregated — aggregated heatmap grid */
export async function getHeatmapAggregated(nodeId: string, cameraId: string, from: string, to: string) {
  const params = new URLSearchParams({ camera_id: cameraId, from, to });
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/heatmap/aggregated?${params}`);
  return unwrap(resp.data.data);
}

/**
 * GET /monitoring/local-nodes/:nodeId/analytics/heatmap/cameras — per-camera heatmap coverage
 * for the Heatmap report. One row per camera, no spatial grids; `from`/`to` are epoch ms.
 */
export async function getHeatmapCameraSummary(nodeId: string, params?: { from?: number; to?: number }) {
  const query = new URLSearchParams();
  if (params?.from) query.append('from', String(params.from));
  if (params?.to) query.append('to', String(params.to));
  const qs = query.toString();
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/heatmap/cameras${qs ? `?${qs}` : ''}`);
  // Two envelopes: the cloud backend's, then the node's — hence `data.data` before `cameras`.
  return unwrap(resp.data.data)?.cameras || [];
}

/** GET /monitoring/local-nodes/:nodeId/analytics/heatmap/metrics — list heatmap metrics */
export async function getHeatmapMetrics(nodeId: string, params?: Record<string, any>) {
  const query = params ? '?' + new URLSearchParams(params as any).toString() : '';
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/heatmap/metrics${query}`);
  return resp.data;
}

/** Get the latest heatmap snapshot image as a blob URL */
export async function getHeatmapImage(nodeId: string, cameraId: string): Promise<string> {
  const resp = await api.get(`/monitoring/local-nodes/${nodeId}/analytics/heatmap/image/${cameraId}`, {
    responseType: 'blob',
  });
  return URL.createObjectURL(resp.data);
}

/** Get URL for the latest heatmap snapshot image (returns empty string as placeholder — use getHeatmapImage instead) */
export function getHeatmapImageUrl(nodeId: string, cameraId: string): string {
  // This is a convenience for cases where you need a static URL
  // For actual usage, prefer getHeatmapImage() which handles auth
  return '';
}

// ─── Model Assignments ───────────────────────────────────────

/** GET /nodes/:nodeId/models/assignments/all */
export async function getNodeModelAssignments(nodeId: string) {
  const resp = await api.get(`/nodes/${nodeId}/models/assignments/all`);
  return unwrap(resp.data.data);
}

/** PUT /nodes/:nodeId/models/assignments/bulk */
export async function bulkSaveNodeModelAssignments(nodeId: string, payload: any) {
  const resp = await api.put(`/nodes/${nodeId}/models/assignments/bulk`, payload);
  return unwrap(resp.data.data);
}

// ─── Face / Person Registration (Cloud FE → Cloud BE → Tunnel → Local Command-Center) ───
// Standalone faces live in the persons registry; employees live in the attendance module.
// Both hand vectors off to the external AI module over Redis (ai:faces:enroll) — the CC
// never stores embeddings. Status (vector_registered) is written back by the AI module.

/** GET /nodes/:nodeId/analytics/persons — list registered persons (the face registry). Returns the rows array. */
export async function getNodePersons(nodeId: string, params?: Record<string, any>) {
  const query = params ? '?' + new URLSearchParams(params as any).toString() : '';
  const resp = await api.get(`/nodes/${nodeId}/analytics/persons${query}`);
  const rows = unwrap(resp.data.data);
  return Array.isArray(rows) ? rows : [];
}

/** POST /nodes/:nodeId/analytics/persons/from-event — register a NEW non-employee person from a face event */
export async function registerNodePersonFromEvent(nodeId: string, data: { event_id: string; name: string; label?: string; notes?: string }) {
  const resp = await api.post(`/nodes/${nodeId}/analytics/persons/from-event`, data);
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/analytics/persons/:personId/link-from-event — attach a face event to an existing person */
export async function linkNodePersonFromEvent(nodeId: string, personId: string, data: { event_id: string }) {
  const resp = await api.post(`/nodes/${nodeId}/analytics/persons/${personId}/link-from-event`, data);
  return unwrap(resp.data.data);
}

/** DELETE /nodes/:nodeId/analytics/persons/:personId — delete a registered person (vectors removed from AI module) */
export async function deleteNodePerson(nodeId: string, personId: string) {
  const resp = await api.delete(`/nodes/${nodeId}/analytics/persons/${personId}`);
  return unwrap(resp.data.data);
}

/** GET /nodes/:nodeId/analytics/persons/:personId/faces — list a person's enrolled faces. Returns the rows array. */
export async function getNodePersonFaces(nodeId: string, personId: string) {
  const resp = await api.get(`/nodes/${nodeId}/analytics/persons/${personId}/faces`);
  const rows = unwrap(resp.data.data);
  return Array.isArray(rows) ? rows : [];
}

/** DELETE /nodes/:nodeId/analytics/persons/:personId/faces/:photoId — unlink one face (AI module drops the vector) */
export async function unlinkNodePersonFace(nodeId: string, personId: string, photoId: string) {
  const resp = await api.delete(`/nodes/${nodeId}/analytics/persons/${personId}/faces/${photoId}`);
  return unwrap(resp.data.data);
}

/** POST /nodes/:nodeId/analytics/attendance/employees/from-event — register a NEW employee from a face event */
export async function registerNodeEmployeeFromEvent(nodeId: string, data: { event_id: string; name: string; employee_code?: string }) {
  const resp = await api.post(`/nodes/${nodeId}/analytics/attendance/employees/from-event`, data);
  return unwrap(resp.data.data);
}

export { api };
export default api;
